import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useModal from "hooks/modals/useModal";

import {
  GetProductUnits,
  PostProductUnit,
  PutProductUnit,
  DeleteProductUnit,
} from "api/rpc/2024-04/facilityAdmin/product/unit";
import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Input from "components/form/input";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";
import FormLayout from "components/form/FormLayout";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { TProductUnit } from "redux/reducers/models/product";

export default function ProductUnits() {
  const dispatch = useAppDispatch();

  const [units, setUnits] = useState<TProductUnit[]>(undefined);
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>("");

  const {
    state: createUnitModal,
    updateModal: updateCreateUnitModal,
    closeModal: closeCreateUnitModal,
  } = useModal({
    name: "",
    abbreviation: "",
  });

  const {
    state: editUnitModal,
    updateModal: updateEditUnitModal,
    closeModal: closeEditUnitModal,
  } = useModal({
    id: null,
    name: "",
    abbreviation: "",
  });

  const { state: deleteUnitModal, updateModal: updateDeleteUnitModal } = useModal({
    id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadUnits(source.token);

    return () => source.cancel();
  }, [search]);

  async function loadUnits(token?: CancelToken) {
    setUnits(undefined);

    const unitRes = await GetProductUnits({ search }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading product units")));
      return;
    }

    setUnits(unitRes.data);
  }

  async function createUnit() {
    if (createUnitModal.name === "") {
      dispatch(showError(t("Product unit name is required")));
      return;
    }

    const unitRes = await PostProductUnit(
      {
        name: createUnitModal.name,
        abbreviation: createUnitModal.abbreviation !== "" ? createUnitModal.abbreviation : undefined,
      },
      true,
    );

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating product unit")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("Product unit created successfully")));
    updateCreateUnitModal({ isOpen: false, name: "", abbreviation: "" });
  }

  async function updateUnit() {
    if (editUnitModal.id === null) {
      dispatch(showError(t("No product unit selected")));
      return;
    }

    if (editUnitModal.name === "") {
      dispatch(showError(t("Product unit name is required")));
      return;
    }

    const unitRes = await PutProductUnit(
      {
        id: editUnitModal.id,
        name: editUnitModal.name,
        abbreviation: editUnitModal.abbreviation !== "" ? editUnitModal.abbreviation : undefined,
      },
      true,
    );

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating product unit")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("Product unit updated successfully")));
    updateEditUnitModal({ isOpen: false, id: null, name: "", abbreviation: "" });
  }

  async function deleteUnit() {
    if (deleteUnitModal.id === null) {
      dispatch(showError(t("No product unit selected")));
      return;
    }

    const unitRes = await DeleteProductUnit({ id: deleteUnitModal.id }, true);

    if (unitRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting product unit")));
      return;
    }

    void loadUnits();
    dispatch(showSuccess(t("Product unit deleted successfully")));
  }

  const primaryAction = {
    content: t("New Unit"),
    action: () => updateCreateUnitModal({ isOpen: true }),
  };

  return (
    <Page title={t("Product Units")} primaryAction={primaryAction} narrow>
      {" "}
      <div className="mb-4">
        <Input
          value={search}
          labelHidden
          placeholder={t("Search product units...")}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <DataTable
        columns={[
          { label: t("Name") },
          { label: t("Abbreviation") },
          { label: "", width: "10%" },
        ]}
        loading={units === undefined}
      >
        {units?.map(unit => (
          <tr key={unit.id}>
            <td>{unit.name}</td>
            <td>{unit.abbreviation}</td>
            <td>
              <div className="flex gap-2">
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`edit-product-unit`}>{t('Edit')}</Tooltip>}
                >
                  <FontAwesomeIcon
                    icon={["far", "pencil"]}
                    onClick={() => updateEditUnitModal({ isOpen: true, ...unit })}
                  />
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`delete-product-unit`}>{t('Delete')}</Tooltip>}
                >
                  <FontAwesomeIcon
                    icon={["far", "trash"]}
                    onClick={() => updateDeleteUnitModal({ isOpen: true, id: unit.id })}
                  />
                </OverlayTrigger>
              </div>
            </td>
          </tr>
        ))}
      </DataTable>
      <Portal isMounted={createUnitModal.isOpen}>
        <Sheet
          open={createUnitModal.isOpen}
          title={t("New Product Unit")}
          size="small"
          onCancel={closeCreateUnitModal}
          okText={t("Create")}
          onOk={createUnit}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={createUnitModal.name}
                label={t("Name")}
                onChange={e => updateCreateUnitModal({ name: e.target.value })}
              />
              <Input
                value={createUnitModal.abbreviation}
                label={t("Abbreviation")}
                onChange={e => updateCreateUnitModal({ abbreviation: e.target.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
      <Portal isMounted={editUnitModal.isOpen}>
        <Sheet
          open={editUnitModal.isOpen}
          title={t("Edit Product Unit")}
          size="small"
          onCancel={closeEditUnitModal}
          okText={t("Update")}
          onOk={updateUnit}
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={editUnitModal.name}
                label={t("Name")}
                onChange={e => updateEditUnitModal({ name: e.target.value })}
              />
              <Input
                value={editUnitModal.abbreviation}
                label={t("Abbreviation")}
                onChange={e => updateEditUnitModal({ abbreviation: e.target.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
      <Portal isMounted={deleteUnitModal.isOpen}>
        <Popup
          open={deleteUnitModal.isOpen}
          type="warning"
          title={t("Delete Product Unit?")}
          description={t("Are you sure you want to delete this product unit?")}
          okText={t("Delete")}
          onOk={deleteUnit}
          onCancel={() => updateDeleteUnitModal({ isOpen: false, id: null })}
        />
      </Portal>
    </Page>
  );
}
